import {createSelector} from 'reselect';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {MODERATION_FLAGGED_OPERATION} from '@packages/systems/ai/agent/shared';
import {transformEventsToMessages} from './utils/transformEventsToMessages';
import {shouldEventShowThinking} from './utils/shouldEventShowThinking';
import {getThinkingLabel} from './utils/getThinkingLabel';
import type {
  AssistantCmsCollection,
  ClientFrameworkEvent,
} from '@packages/domains/ai-assistant/data-types';
import type {Message, CodeStreamingState, DraftAttachment} from './types';

export type CompletedToolEvent = ClientFrameworkEvent & {
  type: 'tool_status';
  status: 'executed';
  tool: string;
  id: string;
};

export const selectAIAssistantStreamState = createSelector(
  (state: DesignerStores) => state.AIAssistantStore.conversationId,
  (state: DesignerStores) => state.AIAssistantStore.isSegmentActive,
  (state: DesignerStores) => state.AIAssistantStore.pendingMessage,
  (state: DesignerStores) => state.AIAssistantStore.shouldCancelStream,
  (state: DesignerStores) => state.AIAssistantStore.pendingExternalToolResponse,
  (
    conversationId,
    isSegmentActive,
    pendingMessage,
    shouldCancelStream,
    pendingExternalToolResponse
  ) => ({
    conversationId,
    isSegmentActive,
    pendingMessage,
    shouldCancelStream,
    pendingExternalToolResponse,
  })
);

export const selectAIAssistantEvents = createSelector(
  (state: DesignerStores) => state.AIAssistantStore.events,
  (events) => events || []
);

export const selectAIAssistantActiveTaskLabel = createSelector(
  selectAIAssistantEvents,
  (events) => {
    // Reasoning and text events can arrive while a tool is still running, so
    // the last event alone does not reliably describe the active task.
    const terminalToolCallIds = new Set<string>();

    for (let index = events.length - 1; index >= 0; index--) {
      const event = events[index];
      if (!event || event.type === 'user_input') break;

      if (
        event.type === 'error' ||
        event.type === 'final' ||
        event.type === 'interrupted'
      ) {
        return undefined;
      }

      if (event.type === 'tool_status') {
        if (event.status !== 'executing') {
          terminalToolCallIds.add(event.callId);
          continue;
        }

        if (!terminalToolCallIds.has(event.callId)) {
          return getThinkingLabel(event);
        }
      }

      if (
        event.type === 'tool_partial' &&
        (!event.toolCallId || !terminalToolCallIds.has(event.toolCallId))
      ) {
        const label = getThinkingLabel(event);
        if (label) return label;
      }
    }

    return undefined;
  }
);

/**
 * Selector for AI Assistant messages (parsed from events)
 * Components should use this instead of transforming events manually
 */
export const selectAIAssistantMessages = createSelector(
  selectAIAssistantEvents,
  (events) => transformEventsToMessages(events)
);

/**
 * Selector for completed tool events that may need side effects
 * Filters for tool_status events with 'executed' status
 */
export const selectCompletedToolEvents = createSelector(
  selectAIAssistantEvents,
  (events: ClientFrameworkEvent[]): CompletedToolEvent[] => {
    return events.filter(
      (event): event is CompletedToolEvent =>
        event.type === 'tool_status' && event.status === 'executed'
    );
  }
);

/**
 * Selector for AI Assistant UI state
 * Used by components for rendering UI controls (input, thinking indicator, etc.)
 */
export const selectAIAssistantUIState = createSelector(
  (state: DesignerStores) => state.AIAssistantStore.conversationId,
  (state: DesignerStores) => state.AIAssistantStore.isUsersTurn,
  (state: DesignerStores) => state.AIAssistantStore.error,
  (state: DesignerStores) => state.AIAssistantStore.isSegmentActive,
  (state: DesignerStores) => state.AIAssistantStore.pendingMessage,
  (state: DesignerStores) => state.AIAssistantStore.braintrustTraceId,
  (state: DesignerStores) => state.AIAssistantStore.spanId,
  (state: DesignerStores) => state.AIAssistantStore.lastInterruptedMessageText,
  selectAIAssistantEvents,
  selectAIAssistantMessages,
  (
    conversationId,
    isUsersTurn,
    error,
    isSegmentActive,
    pendingMessage,
    braintrustTraceId,
    spanId,
    lastInterruptedMessageText,
    events,
    messages
  ) => {
    // Derive shouldShowThinking from last event
    const lastEvent = events[events.length - 1];
    const lastMessage = messages?.[messages.length - 1];
    const isToolMessageShowing = ['tool', 'external_tool_call'].includes(
      lastMessage?.type ?? ''
    );

    const isIncompleteToolPartial =
      lastEvent?.type === 'tool_partial' &&
      !(lastEvent as {isComplete?: boolean}).isComplete;

    const shouldShowThinking =
      (isSegmentActive && shouldEventShowThinking(lastEvent)) ||
      // Show thinking when tool_partial events are streaming BUT only if
      // args are still incomplete and no tool UI is showing
      (isIncompleteToolPartial && !isUsersTurn && !isToolMessageShowing) ||
      // Show thinking immediately when user submits, before SSE stream starts
      (Boolean(pendingMessage) && !isUsersTurn);

    // If there's a pending message, add it optimistically to the messages array
    let displayMessages = messages ?? [];
    if (pendingMessage) {
      // Show optimistic message until user_input event clears pendingMessage
      // Use stable ID to prevent React re-mounting the message on every render
      const optimisticMessage: Message = {
        id: `pending-${conversationId}`,
        role: 'user',
        type: 'text',
        content: pendingMessage.text,
        metadata: pendingMessage.metadata,
        images: pendingMessage.images?.map((img) => ({
          url: img.url,
          detail: 'auto',
          originalFileName: img.originalFileName,
        })),
      };
      displayMessages = [...displayMessages, optimisticMessage];
    }

    const thinkingLabel = shouldShowThinking
      ? Boolean(pendingMessage) && !isSegmentActive
        ? undefined // Default "Thinking..." label during pre-stream gap
        : getThinkingLabel(lastEvent)
      : undefined;

    // Suppress the store-level error banner when a moderation_flagged error
    // event exists in the stream — the inline ErrorEventElement handles it.
    const hasModerationFlaggedEvent = events.some(
      (e) =>
        e.type === 'error' &&
        (e as {context?: {operation?: string}}).context?.operation ===
          MODERATION_FLAGGED_OPERATION
    );
    const effectiveError = hasModerationFlaggedEvent ? null : error;

    return {
      conversationId,
      isUsersTurn,
      shouldShowThinking,
      thinkingLabel,
      error: effectiveError,
      isSegmentActive,
      messages: displayMessages,
      braintrustTraceId,
      spanId,
      wasLastMessageInterrupted: lastInterruptedMessageText !== null,
      lastInterruptedMessageText,
    };
  }
);

/**
 * Selector for full AI Assistant store state
 * Primarily for debugging or comprehensive state access
 */
export const selectAIAssistantStore = (state: DesignerStores) =>
  state.AIAssistantStore;

/**
 * Selector for code streaming state
 * Used by CodeComponentEditor to display streaming code in real-time
 */
export const selectCodeStreamingState = (state: {
  AIAssistantStore: {codeStreaming: CodeStreamingState};
}) => state.AIAssistantStore.codeStreaming;

/**
 * Selector for whether the agent's turn is in progress (thinking or generating).
 */
export const selectIsAgentTurnInProgress = (state: {
  AIAssistantStore: {isUsersTurn: boolean};
}) => !state.AIAssistantStore.isUsersTurn;

/**
 * Selector for draft message text in the chat textarea.
 * Persisted in the Flux store so it survives component unmounts (e.g. Preview toggle).
 */
export const selectAIAssistantDraftMessage = (state: DesignerStores) =>
  state.AIAssistantStore.draftMessage;

/**
 * Selector for draft attachments in the chat textarea.
 * Persisted in the Flux store so they survive component unmounts (e.g. Preview toggle).
 */
export const selectAIAssistantDraftAttachments = (
  state: DesignerStores
): DraftAttachment[] => state.AIAssistantStore.draftAttachments;

/**
 * Selector for CMS collection schemas selected via the "Add content" picker.
 * Source of truth for the AICC CMS picker selection.
 */
export const selectAiaSelectedCmsCollections = (
  state: DesignerStores
): AssistantCmsCollection[] => state.AIAssistantStore.selectedCmsCollections;

export type QuestionOption = {label: string; description?: string};

/**
 * Selector for the pending `ask-user-question` tool call. Returns `null`
 * when nothing is pending or when the pending tool is anything else.
 */
export const selectAIAssistantPendingQuestion = createSelector(
  (state: DesignerStores) => state.AIAssistantStore.pendingExternalToolCall,
  (state: DesignerStores) => state.AIAssistantStore.conversationId,
  (pendingCall, conversationId) => {
    if (!conversationId) return null;
    if (!pendingCall || pendingCall.tool?.name !== 'ask-user-question') {
      return null;
    }

    const args =
      (pendingCall.request?.args as Record<string, unknown> | undefined) ?? {};
    return {
      conversationId,
      toolCallEvent: pendingCall,
      question: typeof args.question === 'string' ? args.question : '',
      reason: typeof args.reason === 'string' ? args.reason : '',
      options: parseOptions(args.options),
      multiSelect: args.multiSelect === true,
    };
  }
);

function parseOptions(raw: unknown): QuestionOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const label = (item as {label?: unknown}).label;
    if (typeof label !== 'string' || label.length === 0) return [];
    const description = (item as {description?: unknown}).description;
    return [
      {
        label,
        ...(typeof description === 'string' ? {description} : {}),
      },
    ];
  });
}
